import { Injectable } from '@angular/core';

export interface StorageEstimateResult {
  usage: number;
  quota: number;
  percentUsed: number;
  usageMB: number;
  quotaMB: number;
}

export const DEFAULT_DB_NAME = 'gars_indexed_db';
export const DEFAULT_DB_VERSION = 2;
export const DEFAULT_STATE_STORE = 'gars_app_state';
export const DEFAULT_CACHE_STORE = 'gars_cache';
export const DEFAULT_REPORTS_STORE = 'gars_reports';

export interface CacheEntry<T = unknown> {
  key: string;
  data: T;
  savedAt: number;
  expiresAt?: number;
}

@Injectable({ providedIn: 'root' })
export class IndexedDbStorageService {
  private dbPromise: Promise<IDBDatabase> | null = null;
  private readonly memoryFallback = new Map<string, Map<string, unknown>>();

  constructor() {
    this.memoryFallback.set(DEFAULT_STATE_STORE, new Map());
    this.memoryFallback.set(DEFAULT_CACHE_STORE, new Map());
    this.memoryFallback.set(DEFAULT_REPORTS_STORE, new Map());
  }

  /**
   * Whether IndexedDB is natively supported in the current runtime environment.
   */
  isSupported(): boolean {
    return typeof window !== 'undefined' && 'indexedDB' in window;
  }

  /**
   * Open or retrieve the cached singleton connection to the IndexedDB database.
   */
  private getDb(): Promise<IDBDatabase> {
    if (!this.isSupported()) {
      return Promise.reject(new Error('IndexedDB is not supported in this environment.'));
    }

    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      try {
        const request = window.indexedDB.open(DEFAULT_DB_NAME, DEFAULT_DB_VERSION);

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;

          if (!db.objectStoreNames.contains(DEFAULT_STATE_STORE)) {
            db.createObjectStore(DEFAULT_STATE_STORE);
          }
          if (!db.objectStoreNames.contains(DEFAULT_CACHE_STORE)) {
            db.createObjectStore(DEFAULT_CACHE_STORE);
          }
          if (!db.objectStoreNames.contains(DEFAULT_REPORTS_STORE)) {
            db.createObjectStore(DEFAULT_REPORTS_STORE);
          }
        };

        request.onsuccess = () => {
          const db = request.result;
          db.onversionchange = () => {
            db.close();
            this.dbPromise = null;
          };
          resolve(db);
        };

        request.onerror = () => {
          console.warn('[IndexedDbStorageService] Failed to open IndexedDB, falling back to in-memory store:', request.error);
          this.dbPromise = null;
          reject(request.error);
        };
      } catch (err) {
        console.warn('[IndexedDbStorageService] Exception opening IndexedDB:', err);
        this.dbPromise = null;
        reject(err);
      }
    });

    return this.dbPromise;
  }

  /**
   * Retrieve an item from the given store by key.
   */
  async get<T>(key: string, storeName: string = DEFAULT_STATE_STORE): Promise<T | null> {
    if (!this.isSupported()) {
      return (this.memoryFallback.get(storeName)?.get(key) as T) ?? null;
    }

    try {
      const db = await this.getDb();
      if (!db.objectStoreNames.contains(storeName)) {
        return (this.memoryFallback.get(storeName)?.get(key) as T) ?? null;
      }
      return await new Promise<T | null>((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const req = store.get(key);

        req.onsuccess = () => {
          resolve(req.result !== undefined ? (req.result as T) : null);
        };
        req.onerror = () => reject(req.error);
      });
    } catch {
      return (this.memoryFallback.get(storeName)?.get(key) as T) ?? null;
    }
  }

  /**
   * Set/overwrite an item in the given store by key.
   */
  async set<T>(key: string, value: T, storeName: string = DEFAULT_STATE_STORE): Promise<void> {
    // Keep memory fallback in sync for immediate access
    let map = this.memoryFallback.get(storeName);
    if (!map) {
      map = new Map();
      this.memoryFallback.set(storeName, map);
    }
    map.set(key, value);

    if (!this.isSupported()) return;

    try {
      const db = await this.getDb();
      if (!db.objectStoreNames.contains(storeName)) {
        return;
      }
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const req = store.put(value, key);

        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch (err) {
      console.warn(`[IndexedDbStorageService] Error setting key "${key}" in store "${storeName}":`, err);
    }
  }

  /**
   * Delete an item by key from the given store.
   */
  async delete(key: string, storeName: string = DEFAULT_STATE_STORE): Promise<void> {
    this.memoryFallback.get(storeName)?.delete(key);

    if (!this.isSupported()) return;

    try {
      const db = await this.getDb();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const req = store.delete(key);

        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch (err) {
      console.warn(`[IndexedDbStorageService] Error deleting key "${key}":`, err);
    }
  }

  /**
   * Clear all items from a given store.
   */
  async clear(storeName: string = DEFAULT_STATE_STORE): Promise<void> {
    this.memoryFallback.get(storeName)?.clear();

    if (!this.isSupported()) return;

    try {
      const db = await this.getDb();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const req = store.clear();

        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch (err) {
      console.warn(`[IndexedDbStorageService] Error clearing store "${storeName}":`, err);
    }
  }

  /**
   * Return all keys in the given store.
   */
  async keys(storeName: string = DEFAULT_STATE_STORE): Promise<string[]> {
    if (!this.isSupported()) {
      return Array.from(this.memoryFallback.get(storeName)?.keys() ?? []);
    }

    try {
      const db = await this.getDb();
      return await new Promise<string[]>((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const req = store.getAllKeys();

        req.onsuccess = () => resolve((req.result as string[]) ?? []);
        req.onerror = () => reject(req.error);
      });
    } catch {
      return Array.from(this.memoryFallback.get(storeName)?.keys() ?? []);
    }
  }

  /**
   * Return all values stored in the given store.
   */
  async getAll<T>(storeName: string = DEFAULT_STATE_STORE): Promise<T[]> {
    if (!this.isSupported()) {
      return Array.from(this.memoryFallback.get(storeName)?.values() as Iterable<T> ?? []);
    }

    try {
      const db = await this.getDb();
      return await new Promise<T[]>((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const req = store.getAll();

        req.onsuccess = () => resolve((req.result as T[]) ?? []);
        req.onerror = () => reject(req.error);
      });
    } catch {
      return Array.from(this.memoryFallback.get(storeName)?.values() as Iterable<T> ?? []);
    }
  }

  /**
   * Return total count of entries in the given store.
   */
  async count(storeName: string = DEFAULT_STATE_STORE): Promise<number> {
    if (!this.isSupported()) {
      return this.memoryFallback.get(storeName)?.size ?? 0;
    }

    try {
      const db = await this.getDb();
      return await new Promise<number>((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const req = store.count();

        req.onsuccess = () => resolve(req.result ?? 0);
        req.onerror = () => reject(req.error);
      });
    } catch {
      return this.memoryFallback.get(storeName)?.size ?? 0;
    }
  }

  /**
   * Cache helper with TTL (time to live) in milliseconds.
   */
  async setCache<T>(key: string, data: T, ttlMs?: number, storeName: string = DEFAULT_CACHE_STORE): Promise<void> {
    const entry: CacheEntry<T> = {
      key,
      data,
      savedAt: Date.now(),
      expiresAt: ttlMs ? Date.now() + ttlMs : undefined
    };
    await this.set(key, entry, storeName);
  }

  /**
   * Retrieve cached item if not expired.
   */
  async getCache<T>(key: string, storeName: string = DEFAULT_CACHE_STORE): Promise<T | null> {
    const entry = await this.get<CacheEntry<T>>(key, storeName);
    if (!entry) return null;
    if (entry.expiresAt && Date.now() > entry.expiresAt) {
      await this.delete(key, storeName);
      return null;
    }
    return entry.data;
  }

  /**
   * Estimate browser storage usage and quota in Bytes and MBs.
   * Demonstrates the massive storage capability of IndexedDB (typically hundreds of MBs/GBs)
   * compared to localStorage (~5MB).
   */
  async getStorageEstimate(): Promise<StorageEstimateResult> {
    if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.estimate) {
      try {
        const estimate = await navigator.storage.estimate();
        const usage = estimate.usage ?? 0;
        const quota = estimate.quota ?? 0;
        const percentUsed = quota > 0 ? (usage / quota) * 100 : 0;
        return {
          usage,
          quota,
          percentUsed: Math.round(percentUsed * 100) / 100,
          usageMB: Math.round((usage / (1024 * 1024)) * 100) / 100,
          quotaMB: Math.round((quota / (1024 * 1024)) * 100) / 100
        };
      } catch {
        // Fallback
      }
    }

    return {
      usage: 0,
      quota: 0,
      percentUsed: 0,
      usageMB: 0,
      quotaMB: 0
    };
  }
}
